import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import api, { apiError } from "@/lib/api";
import { toast } from "sonner";
import SignaturePad from "@/components/SignaturePad";
import { Bus, ShieldCheck, AlertTriangle, Check, KeyRound, Loader2, LogOut } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

const STATES = [
  { key: "ok", label: "Conforme", color: "bg-emerald-500 text-slate-900" },
  { key: "minor", label: "Mineure", color: "bg-amber-500 text-slate-900" },
  { key: "major", label: "Majeure", color: "bg-red-500 text-white" },
];

/** Ronde de sécurité obligatoire : choix de l'autobus + vérification avant départ. */
export default function DriverInspection({ onDone }) {
  const nav = useNavigate();
  const [params] = useSearchParams();
  const back = params.get("back");
  const { logout } = useAuth();
  const [buses, setBuses] = useState([]);
  const [groups, setGroups] = useState([]);
  const [busId, setBusId] = useState(localStorage.getItem("insp_bus") || "");
  const [items, setItems] = useState({});
  const [odometer, setOdometer] = useState("");
  const [notes, setNotes] = useState("");
  const [signature, setSignature] = useState(null);
  const [bypass, setBypass] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/driver/buses").then((r) => setBuses(r.data)).catch(() => {});
    api.get("/driver/inspection/checklist").then((r) => setGroups(r.data.groups)).catch(() => {});
  }, []);

  const total = groups.reduce((n, g) => n + g.items.length, 0);
  const filled = Object.keys(items).length;
  const majors = Object.values(items).filter((v) => v === "major").length;

  const submit = async () => {
    if (!busId) return toast.error("Choisissez votre autobus");
    setBusy(true);
    try {
      const { data } = await api.post("/driver/inspection", {
        bus_id: busId, odometer: odometer === "" ? null : Number(odometer),
        items, notes, signature, bypass_code: bypass || null,
      });
      localStorage.setItem("insp_bus", busId);
      if (!data.can_drive) {
        toast.error(data.message);
      } else {
        toast.success(data.result === "bypass" ? "Ronde contournée — départ autorisé" : data.message);
        if (onDone) onDone(busId);
        else nav(back ? `/driver/${back}` : "/driver");
      }
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <div className="driver-app min-h-screen bg-slate-950 text-white pb-28" data-testid="driver-inspection">
      <div className="px-4 py-3 border-b border-white/10 flex items-center justify-between gap-2">
        <div className="flex items-center gap-2 min-w-0">
          <ShieldCheck className="w-6 h-6 text-yellow-400 shrink-0" />
          <div className="min-w-0">
            <div className="font-display text-lg font-extrabold leading-none">Ronde de sécurité</div>
            <div className="text-[11px] text-white/50 mt-0.5">Obligatoire avant chaque départ · conservée 360 jours</div>
          </div>
        </div>
        <button onClick={async () => { await logout(); nav("/login"); }} data-testid="insp-logout"
          className="driver-tap shrink-0 text-white/60 flex items-center justify-center">
          <LogOut className="w-5 h-5" />
        </button>
      </div>

      <div className="px-3 pt-4 pb-40 space-y-5">
        <div>
          <div className="overline text-white/40 mb-2">1 · Votre autobus</div>
          {!buses.length && <div className="text-sm text-white/60">Aucun autobus dans votre garage.</div>}
          <div className="grid grid-cols-3 gap-2">
            {buses.map((b) => (
              <button key={b.id} onClick={() => setBusId(b.id)} data-testid={`insp-bus-${b.fleet_number}`}
                className={`driver-tap rounded-xl px-2 py-3 font-mono text-sm font-bold border transition-colors ${
                  busId === b.id ? "bg-yellow-400 text-slate-900 border-yellow-400" : "bg-white/5 border-white/15 text-white/80"}`}>
                <Bus className="w-4 h-4 mx-auto mb-1" /> {b.fleet_number}
              </button>
            ))}
          </div>
        </div>

        <div>
          <div className="overline text-white/40 mb-2">2 · Vérification ({filled}/{total})</div>
          <div className="space-y-4">
            {groups.map((g) => (
              <div key={g.group}>
                <div className="text-sm font-display font-bold text-yellow-400 mb-2">{g.group}</div>
                <div className="space-y-2">
                  {g.items.map((it) => (
                    <div key={it.key} className="rounded-xl bg-slate-900 border border-white/10 p-2.5" data-testid={`insp-item-${it.key}`}>
                      <div className="text-[13px] mb-2">{it.label}</div>
                      <div className="grid grid-cols-3 gap-1.5">
                        {STATES.map((s) => (
                          <button key={s.key} onClick={() => setItems((p) => ({ ...p, [it.key]: s.key }))}
                            data-testid={`insp-${it.key}-${s.key}`}
                            className={`h-10 rounded-lg text-xs font-bold border transition-colors ${
                              items[it.key] === s.key ? s.color + " border-transparent" : "bg-white/5 border-white/15 text-white/60"}`}>
                            {s.label}
                          </button>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="space-y-3">
          <div className="overline text-white/40">3 · Odomètre et signature</div>
          <input type="number" inputMode="numeric" value={odometer} onChange={(e) => setOdometer(e.target.value)}
            placeholder="Kilométrage au départ" data-testid="insp-odometer"
            className="w-full h-14 rounded-xl bg-slate-900 border border-white/15 px-4 font-mono text-lg" />
          <textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={2}
            placeholder="Remarques (facultatif)" data-testid="insp-notes"
            className="w-full rounded-xl bg-slate-900 border border-white/15 px-4 py-3 text-sm" />
          <SignaturePad onChange={setSignature} />
        </div>

        <details className="rounded-xl bg-slate-900 border border-white/10 p-3" data-testid="insp-bypass-block">
          <summary className="text-sm text-white/60 flex items-center gap-2 cursor-pointer">
            <KeyRound className="w-4 h-4" /> Contourner avec un code
          </summary>
          <input value={bypass} onChange={(e) => setBypass(e.target.value)} inputMode="numeric"
            placeholder="Code de contournement" data-testid="insp-bypass"
            className="w-full h-12 mt-2 rounded-xl bg-white/5 border border-white/15 px-4 font-mono tracking-widest" />
          <div className="text-[11px] text-white/40 mt-1.5">Le contournement est enregistré et signalé au répartiteur.</div>
        </details>

        {majors > 0 && (
          <div className="rounded-xl bg-red-500/15 border border-red-500/40 p-3 text-sm text-red-200 flex items-start gap-2" data-testid="insp-major-warning">
            <AlertTriangle className="w-5 h-5 shrink-0" />
            {majors} défectuosité(s) majeure(s) : ce véhicule ne pourra pas prendre la route et le répartiteur sera averti.
          </div>
        )}
      </div>

      <div className="fixed bottom-0 left-0 right-0 p-3 bg-slate-950/95 border-t border-white/10">
        <button onClick={submit} disabled={busy} data-testid="insp-submit"
          className="w-full h-16 rounded-2xl bg-emerald-500 text-slate-900 font-display text-xl font-extrabold flex items-center justify-center gap-2 active:scale-95 transition-transform disabled:opacity-50">
          {busy ? <Loader2 className="w-6 h-6 animate-spin" /> : <Check className="w-7 h-7" />}
          CONFIRMER LA RONDE
        </button>
      </div>
    </div>
  );
}
